/**
 * services/web-ui/src/components/audit/StatsCards.tsx
 *
 * W4-C: 4-card stats grid for the ComplianceDashboard.
 *
 * NOTE: The /api/voice/sessions/stats endpoint returns counts but not
 * all_rules_satisfied_pct or top_denied_actions (those are v2 enhancements).
 * Cards display what the endpoint actually provides.
 */

import type { SessionStats } from "../../api/voice-sessions-list";

interface StatsCardsProps {
  stats: SessionStats | null;
}

export function StatsCards({ stats }: StatsCardsProps) {
  const total = stats?.total_sessions ?? 0;
  const successful = stats?.successful_outcomes ?? 0;
  const failed = stats?.failed_outcomes ?? 0;
  const incomplete = stats?.incomplete_outcomes ?? 0;
  const resolved = successful + failed + incomplete;
  const satisfiedPct = resolved > 0 ? Math.round((successful / resolved) * 100) : null;

  const avgDuration = stats?.avg_ended_duration_seconds ?? stats?.avg_duration_seconds ?? null;

  const lastCall = stats?.last_call_at
    ? new Date(stats.last_call_at).toLocaleString()
    : null;

  return (
    <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
      {/* Card 1: Total calls */}
      <StatCard
        label="Total Calls"
        value={total === 0 && stats == null ? "..." : String(total)}
        sub={
          stats
            ? `${stats.active_sessions} active · ${stats.ended_sessions} ended`
            : undefined
        }
      />

      {/* Card 2: Success rate */}
      <StatCard
        label="Task Success"
        value={satisfiedPct != null ? `${satisfiedPct}%` : stats ? "—" : "..."}
        sub={
          stats
            ? `${successful} success · ${failed} failed · ${incomplete} incomplete`
            : undefined
        }
        valueColor={
          satisfiedPct == null
            ? undefined
            : satisfiedPct >= 80
              ? "text-green-400"
              : satisfiedPct >= 50
                ? "text-yellow-400"
                : "text-red-400"
        }
      />

      {/* Card 3: Avg duration */}
      <StatCard
        label="Avg Duration"
        value={
          avgDuration != null
            ? formatDuration(avgDuration)
            : stats
              ? "—"
              : "..."
        }
        sub={lastCall ? `Last call: ${lastCall}` : undefined}
      />

      {/* Card 4: Active now */}
      <StatCard
        label="Active Now"
        value={stats != null ? String(stats.active_sessions) : "..."}
        valueColor={
          stats?.active_sessions
            ? "text-green-400"
            : undefined
        }
        sub={
          stats?.failed_sessions
            ? `${stats.failed_sessions} failed session${stats.failed_sessions !== 1 ? "s" : ""}`
            : "No failures"
        }
      />
    </div>
  );
}

function StatCard({
  label,
  value,
  sub,
  valueColor,
}: {
  label: string;
  value: string;
  sub?: string;
  valueColor?: string;
}) {
  return (
    <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
      <div className="text-xs font-medium text-gray-500 mb-1">{label}</div>
      <div className={`text-2xl font-bold ${valueColor ?? "text-white"} mb-1`}>{value}</div>
      {sub && <div className="text-xs text-gray-500 truncate">{sub}</div>}
    </div>
  );
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}
